/* eslint-disable @typescript-eslint/camelcase */
import { put, takeLatest, call } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { CredRowErrors } from '@card/types';
import {
  Services,
  GetCredentialsResponse,
  ValidateCredentialParams,
  GetBlockedCountResponse,
  getBlockedCount
} from '@card/commonlib';
import { CredentialsActionType, CredentialsActions } from './actions';

type ValidateCredentialsActionType = {
  type: string;
  error: boolean;
  payload: ValidateCredentialParams;
  meta: undefined;
};

type SaveEditedCredentialsActionType = {
  type: string;
  error: boolean;
  payload: {
    idCredential: string;
    password: string;
  };
  meta: undefined;
};

type actionTypeWithCredentialId = {
  type: string;
  error: boolean;
  payload: string;
  meta: undefined;
};

type autoRenewAllCredentialsActionType = {
  type: string;
  error: boolean;
  payload: boolean;
  meta: undefined;
};

type fetchCredentialsActionType = {
  type: string;
  error: boolean;
  payload: {
    page: number;
    per_page: number;
  };
  meta: undefined;
};

type fetchBlockedCredentialsActionType = {
  type: string;
  error: boolean;
  payload: {
    page: number;
    per_page: number;
  };
  meta: undefined;
};

const CredentialExistenceErrors = {
  scraperCreateError: 'scraper_create_error',
  differentAccountError: 'different_account_error',
  missingAttributes: 'missing_attributes'
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getErrorMessage(error: any) {
  let errorMessage: keyof CredRowErrors = 'VALIDATE_FAILED';
  if (
    error &&
    error.response &&
    error.response.data &&
    error.response.data.error &&
    (error.response.data.error === CredentialExistenceErrors.scraperCreateError ||
      error.response.data.error === CredentialExistenceErrors.differentAccountError ||
      error.response.data.error === CredentialExistenceErrors.missingAttributes)
  )
    errorMessage = 'CREDENTIAL_EXISTS';
  return errorMessage;
}

function* validateCredential(actionType: ValidateCredentialsActionType) {
  try {
    yield call(Services.validateCredential, actionType.payload);
    yield put(CredentialsActions.validateSuccess());
  } catch (error) {
    const errorMessage: keyof CredRowErrors = getErrorMessage(error);
    yield put(CredentialsActions.validateFailed(errorMessage));
  }
}

function* saveEditedCredential(actionType: SaveEditedCredentialsActionType) {
  try {
    yield call(Services.editCredential, {
      newPassword: actionType.payload.password,
      idCredential: actionType.payload.idCredential
    });
    yield put(CredentialsActions.validateSuccess());
  } catch (error) {
    const errorMessage: keyof CredRowErrors = getErrorMessage(error);
    yield put(CredentialsActions.validateFailed(errorMessage));
  }
}

function* deleteCredential(actionTypeWithCredentialId: actionTypeWithCredentialId) {
  try {
    yield call(Services.deleteCredential, actionTypeWithCredentialId.payload);
    yield put(CredentialsActions.deleteCredentialSuccess());
  } catch (error) {
    yield put(CredentialsActions.deleteCredentialFailed(error));
  }
}

const CREDENTIAL_STATUS_BLOCKED = ['blocked', 'incorrect', 'warning'];
const CREDENTIAL_STATUS_LIST = ['ok', 'pending'].concat(CREDENTIAL_STATUS_BLOCKED);

function* fetchCredentials(action: fetchCredentialsActionType) {
  const options = {
    ...action.payload,
    filters: { status: CREDENTIAL_STATUS_LIST }
  };
  try {
    const response: GetCredentialsResponse = yield call(Services.getCredentials, options);
    yield put(CredentialsActions.fetchSuccess(response));
  } catch (error) {
    yield put(CredentialsActions.fetchFailed(error));
  }
}

function* fetchCredentialsBlocked(action: fetchBlockedCredentialsActionType) {
  const options = {
    ...action.payload,
    filters: { status: CREDENTIAL_STATUS_BLOCKED }
  };
  try {
    const response: GetCredentialsResponse = yield call(Services.getCredentials, options);
    yield put(CredentialsActions.fetchBlockedSuccess(response));
  } catch (error) {
    yield put(CredentialsActions.fetchBlockedFailed(error));
  }
}

function* enableAutoRenewal(actionTypeWithCredentialId: actionTypeWithCredentialId) {
  try {
    yield call(Services.enableAutomaticRenewal, actionTypeWithCredentialId.payload);
    yield put(CredentialsActions.toggleAutomaticRenewalSuccess());
  } catch (error) {
    yield put(CredentialsActions.toggleAutomaticRenewalFailed(error));
  }
}

function* disableAutoRenewal(actionTypeWithCredentialId: actionTypeWithCredentialId) {
  try {
    yield call(Services.disableAutomaticRenewal, actionTypeWithCredentialId.payload);
    yield put(CredentialsActions.toggleAutomaticRenewalSuccess());
  } catch (error) {
    yield put(CredentialsActions.toggleAutomaticRenewalFailed(error));
  }
}

function* setAutoRenewAllCredentials(action: autoRenewAllCredentialsActionType) {
  const serviceMethod =
    action.payload === true
      ? Services.userEnableAutomaticRenewal
      : Services.userDisableAutomaticRenewal;
  try {
    yield call(serviceMethod);
    yield put(CredentialsActions.autoRenewAllCredentialsSuccess());
  } catch (error) {
    yield put(CredentialsActions.autoRenewAllCredentialsFailed(error));
  }
}

function* getPassword(action: actionTypeWithCredentialId) {
  const credentialId = action.payload;
  try {
    const response: string = yield call(Services.getPassword, credentialId);
    yield put(CredentialsActions.getPasswordSuccess(credentialId, response));
  } catch (error) {
    yield put(CredentialsActions.setError('PASSWORD_FETCH_FAILED' as keyof CredRowErrors));
  }
}

function* getMercadopagoLink() {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response: AxiosResponse<any> = yield call(Services.getMercadoPagoLink);
    yield put(CredentialsActions.getMercadopagoLinkSuccess(response.data.link));
  } catch (error) {
    console.log(error);
  }
}

function* fetchBlockedCount() {
  try {
    const response: GetBlockedCountResponse = yield call(getBlockedCount);
    yield put(CredentialsActions.fetchBlockedCountSuccess(response));
  } catch (error) {
    yield put(CredentialsActions.fetchBlockedCountFailed(error));
  }
}

function* credentialsSagas() {
  yield takeLatest(CredentialsActionType.Validate, validateCredential);
  yield takeLatest(CredentialsActionType.SaveEditedCredential, saveEditedCredential);
  yield takeLatest(CredentialsActionType.Fetch, fetchCredentials);
  yield takeLatest(CredentialsActionType.EnableAutoRenewal, enableAutoRenewal);
  yield takeLatest(CredentialsActionType.DisableAutoRenewal, disableAutoRenewal);
  yield takeLatest(CredentialsActionType.DeleteCredential, deleteCredential);
  yield takeLatest(CredentialsActionType.ToggleAutoRenewAllCredentials, setAutoRenewAllCredentials);
  yield takeLatest(CredentialsActionType.GetPassword, getPassword);
  yield takeLatest(CredentialsActionType.GetMercadopagoLink, getMercadopagoLink);
  yield takeLatest(CredentialsActionType.FetchBlocked, fetchCredentialsBlocked);
  yield takeLatest(CredentialsActionType.FetchBlockedCountStarted, fetchBlockedCount);
}

export default credentialsSagas;
